import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './ErrorSpacePage.css';

declare global {
  interface Window {
    tsParticles?: {
      load: (elementId: string, options: Record<string, unknown>) => Promise<{
        destroy?: () => void;
      }>;
    };
  }
}

interface ErrorSpacePageProps {
  code: '404' | '405';
  title: string;
  description: string;
}

const particlesOptions = {
  fpsLimit: 60,
  particles: {
    number: {
      value: 160,
      density: {
        enable: true,
        area: 800,
      },
    },
    color: {
      value: '#ffffff',
    },
    shape: {
      type: 'circle',
    },
    opacity: {
      value: 1,
      random: {
        enable: true,
        minimumValue: 0.1,
      },
      animation: {
        enable: true,
        speed: 1,
        minimumValue: 0,
        sync: false,
      },
    },
    size: {
      value: 3,
      random: {
        enable: true,
        minimumValue: 1,
      },
    },
    move: {
      enable: true,
      speed: 0.17,
      direction: 'none',
      random: true,
      straight: false,
      outModes: {
        default: 'out',
      },
    },
  },
  interactivity: {
    detectsOn: 'canvas',
    events: {
      resize: false,
    },
  },
  detectRetina: true,
};

const particlesScriptId = 'tsparticles-space-error-script';
const particlesElementId = 'error-space-tsparticles';
const errorSpaceAssetPath = `${process.env.PUBLIC_URL}/error-space`;

const ErrorSpacePage: React.FC<ErrorSpacePageProps> = ({
  code,
  title,
  description,
}) => {
  const navigate = useNavigate();
  const containerRef = useRef<{ destroy?: () => void } | null>(null);
  const [particlesReady, setParticlesReady] = useState(false);
  const canGoBack = window.history.length > 1;

  useEffect(() => {
    let isMounted = true;

    const loadParticles = async () => {
      try {
        await ensureParticlesScript();

        if (!isMounted || !window.tsParticles) {
          return;
        }

        containerRef.current = await window.tsParticles.load(
          particlesElementId,
          particlesOptions
        );
        setParticlesReady(true);
      } catch {
        setParticlesReady(false);
      }
    };

    loadParticles();

    return () => {
      isMounted = false;
      containerRef.current?.destroy?.();
      containerRef.current = null;
    };
  }, []);

  return (
    <main className='error-space'>
      <div id={particlesElementId} className='error-space__particles' />
      {!particlesReady && <div className='error-space__fallback-stars' />}

      <section className='error-space__content' aria-labelledby='error-title'>
        <h1 className='error-space__code'>{code}</h1>
        <h2 id='error-title' className='error-space__title'>
          تائه في <span className='error-space__highlight'>الفضاء</span>؟
          <br />
          {title}
        </h2>
        <p className='error-space__description'>{description}</p>

        <img
          className='error-space__astronaut'
          src={`${errorSpaceAssetPath}/astronaut.svg`}
          alt=''
        />
        <img
          className='error-space__planet'
          src={`${errorSpaceAssetPath}/planet.svg`}
          alt=''
        />

        <div className='error-space__actions' aria-label='إجراءات صفحة الخطأ'>
          <Link
            className='error-space__action error-space__action--primary'
            to='/home'
          >
            العودة للرئيسية
          </Link>
          {canGoBack && (
            <button
              className='error-space__action'
              type='button'
              onClick={() => navigate(-1)}
            >
              الرجوع للخلف
            </button>
          )}
        </div>
      </section>
    </main>
  );
};

const ensureParticlesScript = () => {
  if (window.tsParticles) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(
    particlesScriptId
  ) as HTMLScriptElement | null;

  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = particlesScriptId;
    script.src =
      'https://cdn.jsdelivr.net/npm/tsparticles@2/tsparticles.bundle.min.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.body.appendChild(script);
  });
};

export const NotFoundPage: React.FC = () => (
  <ErrorSpacePage
    code='404'
    title='هذه الصفحة غير موجودة في Class-Tech.'
    description='ربما تغيّر الرابط أو انتقلت الصفحة إلى مدار آخر. يمكنك العودة للرئيسية ومتابعة رحلتك من هناك.'
  />
);

export const MethodNotAllowedPage: React.FC = () => (
  <ErrorSpacePage
    code='405'
    title='هذه الطريقة غير مسموحة هنا.'
    description='المسار موجود، لكن طريقة الوصول الحالية لا تناسب هذه الواجهة. ارجع للرئيسية أو للصفحة السابقة وحاول من جديد.'
  />
);

export default ErrorSpacePage;
